import * as Yup from 'yup';
import { useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
// material
import { Stack, TextField, IconButton, InputAdornment, FormControlLabel, Checkbox, Link, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import LoadingButton from '@mui/lab/LoadingButton';
import {
  BrowserRouter as Router,
  useNavigate,
  Switch,
  Route,
  useParams
} from "react-router-dom";
import axios from '../../../util/axios';
import token from '../../../util/Token';

// ----------------------------------------------------------------------

export default function ResetPasswordForm() {
  let { id, secret,type } = useParams();

  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState();
  const passwordRules = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9]).{8,}$/;

  const resetPasswordSchema = Yup.object().shape({
    password: Yup.string()
      .matches(passwordRules, { message: "Password must contain at least 8 characters, one uppercase, one lowercase, one number and one special character" })
      .required('Password is required'),
    confirmPassword: Yup.string().required('Confirm password is required'),
  });

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: ''
    },
    validationSchema: resetPasswordSchema,
    onSubmit: (resetPasswordData, { setSubmitting, resetForm }) => {
      if (!id || !secret) {
        setError("Invalid reset password link.");
        setSubmitting(false);
        return;
      }
      if (resetPasswordData.password !== resetPasswordData.confirmPassword) {
        setError("Password and confirm password does not match.");
        setSubmitting(false);
        return;
      }
      resetPasswordData['id'] = id;
      if(type==="phone"){
        resetPasswordData['otp'] = secret
      }else resetPasswordData['secret'] = secret;
      (async function () {
        try {
          const registerHelper = async () => {
             const url = type==="phone"?`${process.env.REACT_APP_API_END_POINT}auth/change-password-phone`:`${process.env.REACT_APP_API_END_POINT}auth/change-password`
            const response = await axios(url, {
              method: 'post',
              data: resetPasswordData
            });
            const resetPasswordResponse = response.data.data;
            setSubmitting(false);
            resetForm();
            setSuccessMessage("Password changed successfully, Please login!.");
            // navigate('/login')
          };
          await registerHelper();
        } catch (error) {
          setSubmitting(false);
          if (error.response && error.response.data && error.response.data.message) {
            setError(error.response.data.message);
          }
        }
      })();
      //navigate('/dashboard', { replace: true });
    }
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, resetForm } = formik;

  return (
    <FormikProvider value={formik}>
      <div style={{ color: 'red', textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>{error}</div>
      <div style={{ color: 'green', textAlign: 'center', marginBottom: '16px', fontSize: '14px' }}>{successMessage}</div>
      { !successMessage?
        <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label="Password"
            {...getFieldProps('password')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? "Hide password" : "Show password"} placement="top">
                    <IconButton edge="end" onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          />

          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label="Confirm password"
            {...getFieldProps('confirmPassword')}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showConfirmPassword ? "Hide password" : "Show password"} placement="top">
                    <IconButton edge="end" onClick={() => setShowConfirmPassword((prev) => !prev)}>
                      {showConfirmPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              )
            }}
            error={Boolean(touched.confirmPassword && errors.confirmPassword)}
            helperText={touched.confirmPassword && errors.confirmPassword}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          />
          <Stack direction="row" alignItems="center" justifyContent="center" sx={{ my: 1 }}>
            <Link component={RouterLink} variant="subtitle2" to="/login" sx={{ color: 'primary.main', fontWeight: 600 }}>
              Back to login
            </Link>
          </Stack>

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{
              height: 52,
              fontSize: '1.1rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'none',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
              mt: 2,
              mb: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.5)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                color: 'text.disabled',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            Reset
          </LoadingButton>
        </Stack>
      </Form>:<Stack marginTop={2} alignItems="center">
            <Link component={RouterLink} variant="subtitle2" to="/login" sx={{ color: 'primary.main', fontWeight: 600, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>
              Back to login
            </Link>
          </Stack>}
    </FormikProvider>
  );
}
